import SortableList from '@/components/SortableList.tsx';
import { capitalize } from '@/utils';
import Button from '@superfunnel/ui-core/v2/Button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@superfunnel/ui-core/v2/popover';
import { Table } from '@tanstack/react-table';
import { Minus, Plus, Xmark } from 'iconoir-react';
import { JSX, useEffect, useState } from 'react';

interface ColumnManagementPopover<T> {
  trigger: JSX.Element;
  table: Table<T>;
}
export default function ColumnManagementPopover<T>({
  trigger,
  table,
}: ColumnManagementPopover<T>) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <ColumnManagementPopoverContent
        table={table}
        close={() => setOpen(false)}
      />
    </Popover>
  );
}

interface ColumnManagementPopoverContentProps {
  table: Table<any>;
  close: () => void;
}

interface IColumn {
  id: string;
  name?: string;
  visible: boolean;
}

export function ColumnManagementPopoverContent({
  table,
  close,
}: ColumnManagementPopoverContentProps) {
  const [visibleColumns, setVisibleColumns] = useState<IColumn[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<IColumn[]>([]);

  useEffect(() => {
    setVisibleColumns(
      table
        .getAllLeafColumns()
        .filter((column) => column.getIsVisible())
        .map((column) => ({
          id: column.id,
          name:
            typeof column.columnDef.header === 'string'
              ? column.columnDef.header
              : undefined,
          visible: true,
        })),
    );
    setHiddenColumns(
      table
        .getAllLeafColumns()
        .filter((column) => !column.getIsVisible())
        .map((column) => ({
          id: column.id,
          name:
            typeof column.columnDef.header === 'string'
              ? column.columnDef.header
              : undefined,
          visible: false,
        })),
    );
  }, [table]);

  // Wrapper functions that force update after operations
  const handleColumnReorder = (items: IColumn[]) => {
    table.setColumnOrder(items.map((item) => item.id));
    setVisibleColumns(items);
  };

  const handleToggleVisibility = (column: IColumn, visible: boolean) => {
    table.setColumnVisibility((old) => ({
      ...old,
      [column.id]: visible,
    }));

    if (visible) {
      setVisibleColumns((prev) => [...prev, { ...column, visible: true }]);
      setHiddenColumns((prev) => prev.filter((col) => col.id !== column.id));
    } else {
      setHiddenColumns((prev) => [...prev, { ...column, visible: false }]);
      setVisibleColumns((prev) => prev.filter((col) => col.id !== column.id));
    }
  };

  return (
    <PopoverContent
      className="w-85 overflow-y-auto rounded-[20px] px-2 pt-1 pb-2"
      style={{ maxHeight: 'var(--radix-popover-content-available-height)' }}
      align="end"
      collisionPadding={16}
    >
      <div className="mb-1 flex items-center justify-between px-2 py-1.5">
        <h4 className="text-base-900 font-inter text-base font-medium -tracking-[0.01em]">
          Manage Columns
        </h4>
        <button type={'button'} onClick={() => close()}>
          <Xmark className={'size-5'} />
        </button>
      </div>
      <div className="[&>ul]:space-y-1">
        <SortableList
          key={`visible-columns-${table.getState().columnOrder?.join('-') || 'default'}`}
          itemClassName="bg-base-100 py-0.5 px-2 rounded-lg border-0 min-h-[36px] text-sm -tracking-[0.01em]"
          items={visibleColumns}
          renderItemContent={(column) => {
            return (
              <div className="flex w-full items-center justify-between">
                <span className="flex-1 truncate">
                  {column.name || capitalize(column.id || '') || 'Unknown'}
                </span>
                <Button
                  className={'!p-0'}
                  variant="ghost"
                  onClick={() => handleToggleVisibility(column, false)}
                >
                  <Minus className="size-4" />
                </Button>
              </div>
            );
          }}
          onChange={handleColumnReorder}
          disabledIds={table
            .getAllFlatColumns()
            .filter((column) => column.getIsPinned())
            .map((column) => column.id)}
        />
        {!!hiddenColumns.length && (
          <>
            <div className="relative mt-2.5 mb-3">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="text-base-500 font-inter bg-white px-4 text-sm text-[10px] uppercase">
                  More Columns
                </span>
              </div>
            </div>

            <SortableList
              key={`hidden-columns-${Object.keys(table.getState().columnVisibility || {}).join('-')}`}
              itemClassName="bg-base-50 text-base-500 py-0.5 px-2 rounded-lg border-0 min-h-[36px] text-sm -tracking-[0.01em]"
              items={hiddenColumns}
              renderItemContent={(column) => {
                return (
                  <div className="flex w-full items-center justify-between">
                    <span className="flex-1 truncate">
                      {column.name || capitalize(column.id || '') || 'Unknown'}
                    </span>
                    <Button
                      className={'!p-0'}
                      variant="ghost"
                      onClick={() => handleToggleVisibility(column, true)}
                    >
                      <Plus className="size-4" />
                    </Button>
                  </div>
                );
              }}
              onChange={handleColumnReorder}
              disabledIds={hiddenColumns.map((column) => column.id)}
            />
          </>
        )}
      </div>
    </PopoverContent>
  );
}
